import type { FirebaseApp } from "firebase/app";
import {
  getStorage as getFirebaseSdkStorage,
  ref as firebaseRef,
  uploadBytes as firebaseUploadBytes,
  uploadBytesResumable as firebaseUploadBytesResumable,
} from "firebase/storage";
import type {
  FirebaseStorage,
  FullMetadata,
  StorageReference,
  UploadMetadata,
  UploadResult,
  UploadTask,
} from "firebase/storage";
import type { RuntimeCapabilityRequirement } from "@libs/runtime-contracts";

// A registered gate name: the app that passes its registry checks it.
const STORAGE_WRITE_CAPABILITY = "gcp.storageWrite";

type FirebaseStorageWriteCapabilityRegistry = RuntimeCapabilityRequirement<
  typeof STORAGE_WRITE_CAPABILITY
>;

export type {
  FirebaseStorage,
  FullMetadata,
  StorageReference,
  UploadMetadata,
  UploadResult,
  UploadTask,
};

export function getStorage(app: FirebaseApp): FirebaseStorage {
  return getFirebaseSdkStorage(app);
}

export function ref(storage: FirebaseStorage, path: string): StorageReference {
  return firebaseRef(storage, path);
}

export function uploadBytesWithCapability(args: {
  capabilities: FirebaseStorageWriteCapabilityRegistry;
  ref: StorageReference;
  data: Blob | Uint8Array | ArrayBuffer;
  metadata?: UploadMetadata;
}): Promise<UploadResult> {
  args.capabilities.require(STORAGE_WRITE_CAPABILITY);
  return firebaseUploadBytes(args.ref, args.data, args.metadata);
}

export function uploadBytesResumableWithCapability(args: {
  capabilities: FirebaseStorageWriteCapabilityRegistry;
  ref: StorageReference;
  data: Blob | Uint8Array | ArrayBuffer;
  metadata?: UploadMetadata;
}): UploadTask {
  args.capabilities.require(STORAGE_WRITE_CAPABILITY);
  return firebaseUploadBytesResumable(args.ref, args.data, args.metadata);
}
